/**
 * Preset in-app navigation targets for In-App Message primary actions.
 *
 * Each preset resolves to the same { type, value, params } shape the mobile app
 * expects (InAppMessage.tsx → navigation.navigate). Nested tab routes mirror the
 * proven scansbs:// deep-link patterns in scan-sbs-app-expo/App.tsx.
 */

export const DEFAULT_DESTINATION_ID = 'home';

/** @typedef {{ id: string, icon: string, navigation: { type: 'screen', value: string, params?: object } }} InAppDestination */

/** @type {InAppDestination[]} */
export const IN_APP_MESSAGE_DESTINATIONS = [
  {
    id: 'home',
    icon: 'Home',
    navigation: {
      type: 'screen',
      value: 'AppDrawer',
      params: {
        screen: 'MainTabs',
        params: { screen: 'HomeTab', params: { screen: 'Home' } },
      },
    },
  },
  {
    id: 'chat_list',
    icon: 'MessageSquare',
    navigation: {
      type: 'screen',
      value: 'AppDrawer',
      params: {
        screen: 'MainTabs',
        params: { screen: 'ChatTab', params: { screen: 'ChatList' } },
      },
    },
  },
  {
    id: 'attendance',
    icon: 'Calendar',
    navigation: {
      type: 'screen',
      value: 'AppDrawer',
      params: {
        screen: 'MainTabs',
        params: { screen: 'CalendarTab', params: { screen: 'Attendance' } },
      },
    },
  },
  {
    id: 'settings',
    icon: 'Settings',
    navigation: {
      type: 'screen',
      value: 'AppDrawer',
      params: {
        screen: 'MainTabs',
        params: { screen: 'SettingsTab', params: { screen: 'Settings' } },
      },
    },
  },
  {
    id: 'create_post',
    icon: 'PenSquare',
    navigation: {
      type: 'screen',
      value: 'CreatePost',
    },
  },
  {
    id: 'qr_scanner',
    icon: 'QrCode',
    navigation: {
      type: 'screen',
      value: 'QRCodeScanner',
    },
  },
  {
    id: 'colleagues',
    icon: 'Users',
    navigation: {
      type: 'screen',
      value: 'AllColleagues',
    },
  },
  {
    id: 'permissions',
    icon: 'Shield',
    navigation: {
      type: 'screen',
      value: 'Permissions',
    },
  },
  {
    id: 'wellbeing',
    icon: 'Heart',
    navigation: {
      type: 'screen',
      value: 'Wellbeing',
    },
  },
  {
    id: 'about',
    icon: 'Info',
    navigation: {
      type: 'screen',
      value: 'AboutApp',
    },
  },
  {
    id: 'search',
    icon: 'Search',
    navigation: {
      type: 'screen',
      value: 'Search',
    },
  },
];

const byId = Object.fromEntries(
  IN_APP_MESSAGE_DESTINATIONS.map((d) => [d.id, d]),
);

export function getDestinationById(id) {
  return byId[id] ?? null;
}

export function getDefaultDestinationId() {
  return DEFAULT_DESTINATION_ID;
}

/**
 * Resolve admin primaryAction config → mobile action { type, value, params? }.
 * Supports new destinationId presets and legacy free-text value/params.
 */
export function resolvePrimaryAction(primaryAction) {
  if (!primaryAction) return null;

  if (primaryAction.type === 'link') {
    const value = primaryAction.value?.trim();
    if (!value) return null;
    return { type: 'link', value };
  }

  if (primaryAction.type === 'screen') {
    const dest = primaryAction.destinationId
      ? getDestinationById(primaryAction.destinationId)
      : null;

    if (dest) {
      const { type, value, params } = dest.navigation;
      return {
        type,
        value,
        ...(params ? { params } : {}),
      };
    }

    // Legacy campaigns: free-text screen name + optional JSON params string.
    if (primaryAction.value) {
      let parsedParams;
      if (primaryAction.params) {
        try {
          parsedParams =
            typeof primaryAction.params === 'string'
              ? JSON.parse(primaryAction.params)
              : primaryAction.params;
        } catch {
          parsedParams = undefined;
        }
      }
      return {
        type: 'screen',
        value: primaryAction.value,
        ...(parsedParams && Object.keys(parsedParams).length > 0
          ? { params: parsedParams }
          : {}),
      };
    }
  }

  return null;
}
